<template>
  <div v-if="btSupported">
    <!-- Compact status so the operator can see the station is armed without
         opening anything. A phone that is silently NOT going to print is worse
         than one that never offered to. -->
    <div v-if="entryMode" class="d-flex align-center justify-end flex-wrap">
      <!-- A button, not a switch: this is read across a bench by someone
           holding a bundle, and it says the same thing three ways — a printer
           icon that gets crossed out, colour, and the word. Amber is the state
           that actually costs stickers: switched on with nothing connected,
           which a green/grey toggle would show as "on" while nothing prints. -->
      <v-btn
        v-if="configured"
        :color="autoPrint ? (connected ? 'green' : 'amber darken-2') : ''"
        :dark="autoPrint"
        :outlined="!autoPrint"
        class="ma-1"
        @click="autoPrint = !autoPrint"
      >
        <v-icon left>
          {{ autoPrint ? "mdi-printer" : "mdi-printer-off" }}
        </v-icon>
        {{ autoPrint ? "พิมพ์อัตโนมัติ ON" : "ไม่พิมพ์ OFF" }}
      </v-btn>
      <v-chip
        small
        class="ma-1"
        :color="autoPrint && !connected ? 'amber lighten-4' : ''"
        :outlined="!(autoPrint && !connected)"
        @click="dialog = true"
      >
        <v-icon :left="configured" small>mdi-printer-wireless</v-icon>
        <span v-if="configured">{{ statusText }}</span>
        <v-icon right small>mdi-cog-outline</v-icon>
      </v-chip>
    </div>

    <div
      v-if="status && entryMode"
      class="text-caption grey--text text-right mr-1"
      style="white-space: pre-wrap"
    >
      {{ status }}
    </div>

    <v-dialog v-model="dialog" max-width="420">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left>mdi-printer-wireless</v-icon>
          ตั้งค่าเครื่องพิมพ์ / Printer setup
        </v-card-title>
        <v-card-text>
          <div class="d-flex align-center mb-3">
            <v-btn small color="primary" :loading="busy" @click="connect">
              <v-icon left small>mdi-bluetooth</v-icon>
              {{ connected ? "เชื่อมต่อใหม่ / Reconnect" : "เชื่อมต่อ / Connect" }}
            </v-btn>
            <span class="ml-3 text-caption">
              {{ connected ? printerName : "ยังไม่เชื่อมต่อ / not connected" }}
            </span>
          </div>

          <v-divider class="mb-3" />

          <div class="text-caption mb-1">ขนาดป้าย / Label size</div>
          <v-radio-group v-model="sizeKey" dense hide-details class="mt-0 mb-3">
            <v-radio
              v-for="s in sizes"
              :key="s.key"
              :label="s.title"
              :value="s.key"
            />
          </v-radio-group>

          <v-text-field
            v-model.number="copies"
            type="number"
            min="1"
            max="20"
            dense
            outlined
            hide-details
            class="mb-4"
            label="สำเนา / Copies per entry"
          />

          <div class="text-caption grey--text mt-2">
            เปิดเฉพาะเครื่องที่มีเครื่องพิมพ์อยู่ข้างๆ /
            Turn on only on the phone next to a printer. Settings are kept on
            this phone.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text :disabled="busy || !connected" @click="testPrint">
            <v-icon left small>mdi-file-find-outline</v-icon>
            ทดสอบ / Test print
          </v-btn>
          <v-spacer />
          <v-btn color="primary" text @click="dialog = false">ปิด / Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rendered off-screen rather than hidden: the rasteriser needs real
         layout, and display:none produces a blank label. -->
    <div
      style="position: absolute; left: -10000px; top: 0; pointer-events: none"
      aria-hidden="true"
    >
      <label-sticker
        v-if="pending"
        ref="sticker"
        :entry="pending.entry"
        :worksheet="worksheet"
        :parameters="parameters"
        :qr-data-url="pending.qrDataUrl"
        :lot-index="pending.lotIndex"
        :commodity-name="commodityName"
        :product-info="productInfo"
        :company-name="companyName"
        :company-address="companyAddress"
        :company-phone="companyPhone"
      />
    </div>
  </div>
</template>

<script>
import QRCode from "qrcode";
import env from "@/env";
import { mapActions, mapGetters } from "vuex";
import btPrinter from "@/utils/btPrinter.js";
import { stickerToTsplBytes, clampCopies } from "@/utils/tsplLabel.js";
import { buildQrValue, lotIndexOf } from "@/utils/worksheetLabelQr.js";
import {
  LABEL_SIZES,
  sizeKeyOf,
  loadDeviceConfig,
  saveDeviceConfig,
  isAutoPrintEnabled,
  setAutoPrintEnabled,
  needsRotate90,
  printQuality,
} from "@/utils/labelDeviceConfig.js";

/**
 * Prints one label as soon as an entry is saved, so the sticker goes onto the
 * goods there and then instead of a pile of them being sorted out afterwards.
 *
 * It also removes what made batch printing fragile: the printer is idle when a
 * job arrives, so there is no queue to pace and no way to overrun its buffer.
 *
 * Off unless this phone was deliberately set up for it — most stations have no
 * printer beside them.
 */
export default {
  name: "AutoLabelPrint",
  components: {
    LabelSticker: () =>
      import("@/components/processWorksheet/LabelSticker.vue"),
  },
  props: {
    worksheet: { type: Object, required: true },
    productInfo: { type: Object, default: null },
    // The printer row belongs on the weighing screen. Elsewhere the component
    // is still mounted so a sticker can be reprinted, but shows nothing.
    entryMode: { type: Boolean, default: true },
  },
  data() {
    return {
      btSupported: btPrinter.isSupported(),
      // Most stations have no printer beside them. Until one has been set up on
      // this phone, all that shows is a small icon to ignore — a prominent
      // OFF button on forty phones that cannot print is just something to
      // switch on by mistake and then wonder about.
      configured: btPrinter.hasRememberedPrinter(),
      dialog: false,
      busy: false,
      connected: false,
      printerName: "",
      autoPrint: isAutoPrintEnabled(),
      sizeKey: "",
      copies: 4,
      status: "",
      pending: null,
      sizes: LABEL_SIZES,
      companyName: env.VUE_APP_COMPANY_NAME,
      companyAddress: env.VUE_APP_COMPANY_ADDRESS,
      companyPhone: env.VUE_APP_COMPANY_PHONE,
      isLME: false,
      lmeBrand: "",
      lmeChecked: false,
    };
  },
  computed: {
    ...mapGetters("labelPrintParameters", ["parameters"]),
    ready() {
      return this.autoPrint && this.connected;
    },
    commodityName() {
      return this.parameters.commodityOverride || this.worksheet.productName;
    },
    statusText() {
      if (!this.connected) {
        return "แตะเพื่อตั้งค่า / Tap to set up";
      }
      const size = this.sizes.find((s) => s.key === this.sizeKey);
      return `${this.printerName} · ${size ? size.title : ""} · ×${this.copies}`;
    },
  },
  watch: {
    sizeKey(key) {
      const size = this.sizes.find((s) => s.key === key);
      if (size) {
        this.updateParameters({
          labelWidth: size.labelWidth,
          labelHeight: size.labelHeight,
        });
        saveDeviceConfig(this.parameters);
      }
    },
    copies(value) {
      const n = clampCopies(value);
      this.updateParameters({ copies: n });
      saveDeviceConfig(this.parameters);
    },
    autoPrint(on) {
      setAutoPrintEnabled(on);
      this.status = on
        ? "จะพิมพ์อัตโนมัติเมื่อบันทึก / Will print on each save"
        : "";
    },
  },
  methods: {
    ...mapActions("labelPrintParameters", ["updateParameters"]),
    ...mapActions("messages", ["addErrorMessage"]),

    async connect() {
      this.busy = true;
      try {
        this.printerName = await btPrinter.connect();
        this.connected = true;
        this.configured = true;
        await this.loadLmeContext();
        this.status = `เชื่อมต่อแล้ว / Connected: ${this.printerName}`;
      } catch (error) {
        this.connected = false;
        const msg = error && error.message ? error.message : String(error);
        this.status = /NotFound|cancel/i.test(
          (error && error.name) + " " + msg
        )
          ? "ไม่พบเครื่องพิมพ์? กด FEED หรือปิด-เปิดเครื่องพิมพ์ / Printer not listed? Press FEED or power-cycle it."
          : `เชื่อมต่อไม่ได้ / Connect failed: ${msg}`;
      } finally {
        this.busy = false;
      }
    },

    // Print the most recent entry, or nothing if there is none yet. Proves the
    // station works before anyone relies on it mid-shift.
    async testPrint() {
      const entries = this.worksheet.entries || [];
      if (!entries.length) {
        this.status = "ยังไม่มีรายการให้ทดสอบ / No entry yet to test with";
        return;
      }
      await this.printEntry(entries[entries.length - 1], entries.length);
    },

    /**
     * Print a single label. lotIndex is passed in by the worksheet, which owns
     * the entry list — it is the entry's position, and on LME products it is
     * encoded into the QR, so a wrong one produces a scannable but wrong
     * sticker. Refuse rather than guess.
     */
    // Exposed so the worksheet can warn about renumbering without importing the
    // helper itself — its module graph is circular and adding to it is what
    // blanked the page.
    lotIndexFor(entries, entry) {
      return entry ? lotIndexOf(entries, entry) : 0;
    },

    // Poll rather than guess a delay: the first call has to download the
    // sticker component, later ones are instant.
    async waitForSticker(timeoutMs = 5000) {
      const started = Date.now();
      for (;;) {
        const el = this.$refs.sticker && this.$refs.sticker.$el;
        // A mounted-but-unstyled element has no width yet; wait for layout.
        if (el && el.offsetWidth > 0) {
          await new Promise((r) => setTimeout(r, 120));
          return el;
        }
        if (Date.now() - started > timeoutMs) {
          return null;
        }
        await new Promise((r) => setTimeout(r, 50));
      }
    },

    async printEntry(entry, entries) {
      const lotIndex = this.lotIndexFor(entries, entry);
      if (!entry || !lotIndex) {
        this.status =
          "ไม่ทราบเลขล็อต ไม่พิมพ์ / Lot number unknown — not printed. Use the batch print page.";
        return false;
      }
      if (this.busy) {
        return false;
      }
      this.busy = true;
      try {
        if (!btPrinter.isConnected()) {
          this.printerName = await btPrinter.connect();
          this.connected = true;
        }

        const qrValue = buildQrValue({
          worksheet: this.worksheet,
          entry,
          lotIndex,
          isLME: this.isLME,
          lmeBrand: this.lmeBrand,
          companyName: this.companyName,
        });
        const qrDataUrl = await QRCode.toDataURL(qrValue, {
          margin: 0,
          width: 600,
          errorCorrectionLevel: "M",
        });

        this.pending = { entry, lotIndex, qrDataUrl };
        await this.$nextTick();

        // The sticker is a lazily-loaded component, so on the first print the
        // browser still has to fetch its chunk — a fixed short wait is not
        // enough and the label silently never rendered. Wait for it to actually
        // exist, then give it a moment to lay out (fonts, QR image) so the
        // rasteriser does not catch it half-drawn.
        const el = await this.waitForSticker();
        if (!el) {
          throw new Error("Label did not render");
        }

        const copies = clampCopies(this.parameters.copies);
        const bytes = await stickerToTsplBytes(el, {
          widthMm: Number(this.parameters.labelWidth) || 90,
          heightMm: Number(this.parameters.labelHeight) || 50,
          dpi: 203,
          gapMm: 2,
          direction: this.parameters.rotate ? 1 : 0,
          rotate90: needsRotate90(this.parameters),
          ...printQuality(),
          copies,
        });
        await btPrinter.write(bytes);
        this.status = `พิมพ์แล้ว / Printed ${this.worksheet.documentNumber}-${lotIndex}${
          copies > 1 ? ` ×${copies}` : ""
        }`;
        return true;
      } catch (error) {
        this.connected = btPrinter.isConnected();
        const msg = error && error.message ? error.message : String(error);
        this.status = `พิมพ์ไม่สำเร็จ / Print failed at ${
          this.worksheet.documentNumber
        }-${lotIndex}: ${msg} — ใช้หน้าพิมพ์ทั้งชุด / use the batch print page`;
        return false;
      } finally {
        this.pending = null;
        this.busy = false;
      }
    },

    // LME products encode the lot number into the QR itself, so the label needs
    // to know which kind of product this worksheet is.
    //
    // Called when a printer is connected, NEVER on page load. A failed request
    // here is not local: a 401/403 makes the global axios interceptor log the
    // user out, and this component sits on the worksheet every operator uses.
    // Running it on load turned "open a worksheet" into "lose your session",
    // and the next thing anyone noticed was that saving an entry did nothing.
    async loadLmeContext() {
      if (this.lmeChecked) {
        return;
      }
      this.lmeChecked = true;
      if (!this.worksheet.productName) {
        return;
      }
      try {
        const response = await this.$axios.get("/api/lme/records/brands");
        const brands = Array.isArray(response.data) ? response.data : [];
        const match = brands.find(
          (b) => b.internalProductName === this.worksheet.productName
        );
        if (match) {
          this.isLME = true;
          this.lmeBrand = match.lmeBrand;
        }
      } catch (error) {
        // No LME context — treat as a normal product.
      }
    },
  },
  created() {
    const config = loadDeviceConfig();
    if (config) {
      this.updateParameters(config);
    }
    this.sizeKey = sizeKeyOf(this.parameters) || LABEL_SIZES[0].key;
    this.copies = clampCopies(this.parameters.copies);
    this.connected = btPrinter.isConnected();
    if (this.connected) {
      this.printerName = "printer";
    }
  },
};
</script>
